import { cn } from './utils.js';
import { disabledFade, focusRing } from './focus.js';

/* ===========================================================================
 * The shared skin for anything that behaves like a text field — `<Input>`,
 * `<Select>` and `<Combobox>`. One constant so the three cannot drift and
 * stop lining up when they sit next to each other in a form row.
 *
 * Anatomy (sister app's DESIGN_SYSTEM.md, on Hadir's tokens):
 *   height 40px · 16px inline padding · 6px corners · 1px border · 14px text
 * 40px is deliberately the same as a `size="md"` button, so a field and the
 * button beside it share a baseline.
 *
 * FOCUS. The sister app's treatment is a brand-coloured border plus a soft 3px
 * brand halo at 10% alpha. The geometry is kept; the alpha is raised to 25%
 * because at 10% the halo is invisible against a dark surface. The solid
 * border is what actually carries the 3:1 contrast the focus indicator needs —
 * the halo is the polish on top.
 *
 * TYPE. 14px is the console step, but mobile Safari force-zooms the viewport
 * on any focused field under 16px. Rather than give up the density everywhere
 * to satisfy one browser, the 16px floor is applied only where that browser
 * lives — the same coarse-pointer query that raises the touch target.
 * ======================================================================== */
/* ---------------------------------------------------------------------------
 * The skin is assembled from three pieces rather than written as one literal,
 * because `<Textarea>` wants all of it EXCEPT the fixed `h-10` — a multi-line
 * box is sized by `rows`, and a 40px cap would crop it to a single line.
 *
 * Splitting here rather than re-typing the skin inside `Textarea.tsx` is the
 * whole point of this file: a fourth private copy of "border, corners, focus,
 * invalid, disabled" is exactly the drift this constant was created to stop.
 * The next time the focus ring is retuned, four elements have to move, and
 * only one of them would be in a file anyone thinks to open.
 *
 * The pieces are re-joined BELOW IN THE ORIGINAL ORDER, with `h-10` back in
 * the slot it always occupied. That is not cosmetic: `cn` ends in
 * tailwind-merge, whose output preserves input order, so keeping the sequence
 * keeps `controlClass` byte-identical to what it was before the split — and
 * `<Input>`, `<Select>` and `<Combobox>` therefore emit the exact same class
 * attribute they did before. (Verified by diffing the compiled string.)
 * ------------------------------------------------------------------------ */

/** Border, corners, ground, inline padding and type — everything about the box
 *  except how tall it is. */
const CONTROL_BOX =
  'flex w-full rounded-control border border-input bg-surface px-4 text-copy-lg text-foreground';

/** The coarse-pointer floors: a 44px touch target and the 16px type that stops
 *  mobile Safari force-zooming. Both are minimums, so they compose with a fixed
 *  `h-10` and with a textarea's `rows`-driven height alike. */
const CONTROL_TOUCH =
  '[@media(pointer:coarse)]:min-h-touch [@media(pointer:coarse)]:text-base';

/* ---------------------------------------------------------------------------
 * READ-ONLY — the state the system did not have.
 *
 * There was no read-only treatment at all, so three call sites invented three:
 * `bg-muted/50`, `bg-muted text-muted-foreground` (the disabled skin, exactly),
 * and — on the shifts form — `disabled` used to mean readonly, which is not a
 * styling mistake but a functional one: `disabled` takes the control out of the
 * tab order AND out of form submission, so a value the page itself labels
 * "managed in Settings → …" cannot be reached, focused or copied by keyboard.
 * That is the case this exists to make unnecessary.
 *
 * WHAT IT HAS TO SAY, versus disabled:
 *
 *   disabled   "this control is not available to you"   faded, unreachable
 *   read-only  "this is the value; it is decided elsewhere"  present, focusable,
 *                                                            selectable, copyable
 *
 * So the ink is deliberately NOT faded and NOT muted — a read-only field's
 * value is the whole reason it is on screen, and the disabled treatment's 50%
 * fade plus `text-muted-foreground` is precisely how you make a value look like
 * it does not matter. Nor is the cursor touched: the native I-beam over the
 * text is the affordance that says "you can still select this", and
 * `cursor-not-allowed` would flatly contradict the fact that you can.
 *
 * The two visual signals are therefore:
 *   - `bg-muted` — the same "not a box you type in" ground as disabled.
 *   - the border dropped while unfocused, so it reads as a VALUE ON A PLATE
 *     rather than an empty field someone forgot to fill. `:not(:focus-visible)`
 *     is what keeps that from eating the focus treatment: tab into it and the
 *     box and its `focus-visible:border-primary` come straight back, on top of
 *     the outline every other control shares.
 *
 * ---------------------------------------------------------------------------
 * WHY `[&[readonly]]:` AND NOT TAILWIND'S `read-only:` VARIANT.
 *
 * `read-only:` compiles to the CSS `:read-only` pseudo-class, and `:read-only`
 * does not mean "has the readonly attribute" — it means "is not user-editable",
 * which is a far bigger set. Every `<select>` in the app matches it (a select
 * is never `:read-write`), and so does every DISABLED input. Put on the shared
 * skin below, `read-only:bg-muted` would tint ~40 perfectly ordinary dropdowns
 * grey and would double up on the disabled state it is supposed to be distinct
 * from. The attribute selector matches only what React writes for `readOnly`,
 * which is the actual state being styled. Both forms were compiled against the
 * real config before choosing:
 *
 *   .read-only\:bg-muted:read-only            <- also every <select>, every :disabled
 *   .\[\&\[readonly\]\]\:bg-muted[readonly]   <- only a readonly field
 * ------------------------------------------------------------------------ */
const READ_ONLY = cn(
  '[&[readonly]]:bg-muted',
  '[&[readonly]:not(:focus-visible)]:border-transparent'
);

/** Everything that reacts — transition, placeholder, focus, invalid, disabled,
 *  read-only. None of it is height-dependent, so all four control types share
 *  it whole. */
const CONTROL_STATES = cn(
  'transition-[border-color,box-shadow] duration-fast ease-standard',
  'placeholder:text-muted-foreground',
  // The border change is the FIELD's own signal — it says which box you are
  // about to type in. The halo around it is the shared one.
  'focus-visible:border-primary',
  focusRing,
  // Driven off `aria-invalid` rather than a prop, so the ~dozen call sites
  // that already set it for screen readers get the visual for free.
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:outline-danger',
  cn('disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground', disabledFade),
  READ_ONLY
);

export const controlClass = cn(CONTROL_BOX, 'h-10', CONTROL_TOUCH, CONTROL_STATES);

/**
 * `controlClass` minus the fixed `h-10`, for a control whose height is content-
 * driven. Today that is `<Textarea>` and only `<Textarea>` — a single-line
 * field should keep the 40px baseline it shares with a `size="md"` button, so
 * reach for `controlClass` unless the element genuinely grows.
 *
 * Note this deliberately KEEPS `CONTROL_TOUCH`. `[@media(pointer:coarse)]:
 * min-h-touch` is a floor, not a height, and tailwind-merge treats a variant-
 * prefixed `min-h-*` as a different group from a bare one — so a caller's own
 * `min-h-*` and this floor both survive instead of one silently eating the
 * other.
 */
export const controlClassNoHeight = cn(CONTROL_BOX, CONTROL_TOUCH, CONTROL_STATES);

/**
 * THE FIELD AS A CONTAINER — one box that holds chips AND the input that adds
 * them, rather than a box with its chips stacked underneath it.
 *
 * `Combobox`'s token mode is the one caller. The difference from
 * `controlClassNoHeight` is entirely about WHERE the focus lives: this is a
 * `<div>` whose real control is an `<input>` inside it, so every state that
 * `CONTROL_STATES` hangs off `:focus-visible` on the element itself has to hang
 * off `:focus-within` instead — otherwise the box never lights up, and the
 * reader's caret is in a field with no edge.
 *
 * `min-h-control` and not `h-10`: the whole point is that it grows with the
 * chips inside it. `px-2` rather than the box's `px-4`, because a chip carries
 * its own padding and the doubled inset is what makes a token field look loose.
 */
export const controlShellClass = cn(
  'flex w-full flex-wrap items-center gap-inline',
  'min-h-control rounded-control border border-input bg-surface px-2 py-1',
  'text-copy-lg text-foreground',
  CONTROL_TOUCH,
  'transition-[border-color,box-shadow] duration-fast ease-standard',
  'focus-within:border-primary',
  // `focusRing`'s outline, moved onto the wrapper. Same tokens, same offset —
  // it is the same halo every other control wears, on the element that is
  // actually the field here.
  'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring',
  'aria-[invalid=true]:border-danger',
  'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-muted'
);
